/**
 * The bar across the top of Home: the lockup, File, View and Help, the search box,
 * Import, New and the account.
 */

import { useEffect } from 'react';
import { waitForBridge } from '../desktop/bridge';
import { Button } from '../core/controls';
import {
  checkIcon,
  filePlusIcon,
  presentationChartIcon,
  tableIcon,
  fileTextIcon,
  uploadSimpleIcon,
  plusIcon,
  folderOpenIcon,
  gearIcon
} from '../core/icons';
import type { Appearance } from '../core/appearance';
import { CommandSearch } from '../core/palette/CommandSearch';
import type { Command } from '../core/shell/types';
import { Avatar, Menu, type MenuItem } from '../core/ui';
import { isDesktopUA } from '../desktop/detect';
import { Wordmark } from '../components/Wordmark';
import { Link } from '../router';
import { useApp } from '../state';
import { accountOrigin, HELP_URL } from '../theme';
import type { Layout } from './home';
import type { DocKind } from '@shared/api';

export interface HomeContext {
  create: (kind: DocKind) => void;
  pickFile: () => void;
  openFile?: () => void;
  layout: Layout;
  setLayout: (l: Layout) => void;
  appearance: Appearance;
  setAppearance: (a: Appearance) => void;
}

/** The commands Home's search box runs. Documents are searched in the same box. */
export const HOME_COMMANDS: Command<HomeContext>[] = [
  { id: 'new.docs', title: 'New document', section: 'Create', icon: fileTextIcon, keywords: ['blank', 'word'], run: (c) => c.create('docs') },
  { id: 'new.sheets', title: 'New spreadsheet', section: 'Create', icon: tableIcon, keywords: ['blank', 'excel'], run: (c) => c.create('sheets') },
  { id: 'new.slides', title: 'New presentation', section: 'Create', icon: presentationChartIcon, keywords: ['blank', 'deck', 'powerpoint'], run: (c) => c.create('slides') },
  { id: 'import', title: 'Import a file', section: 'Create', icon: uploadSimpleIcon, keywords: ['open', 'docx', 'xlsx', 'pptx', 'upload'], run: (c) => c.pickFile() },
  { id: 'view.grid', title: 'Show as a grid', section: 'View', keywords: ['cards', 'layout'], run: (c) => c.setLayout('grid') },
  { id: 'view.list', title: 'Show as a list', section: 'View', keywords: ['rows', 'layout'], run: (c) => c.setLayout('list') },
  { id: 'view.theme-system', title: 'Use the system theme', section: 'View', keywords: ['appearance', 'dark', 'light', 'match'], run: (c) => c.setAppearance('system') },
  { id: 'view.theme-light', title: 'Use the light theme', section: 'View', keywords: ['appearance', 'day'], run: (c) => c.setAppearance('light') },
  { id: 'view.theme-dark', title: 'Use the dark theme', section: 'View', keywords: ['appearance', 'night'], run: (c) => c.setAppearance('dark') }
];

function TextMenu({ label, items }: { label: string; items: MenuItem[] }) {
  return (
    <Menu
      label={label}
      icon={plusIcon}
      align="left"
      items={items}
      trigger={({ open, toggle, ref }) => (
        <button ref={ref} type="button" className="mv-home-menu" aria-haspopup="menu" aria-expanded={open} onClick={toggle}>
          {label}
        </button>
      )}
    />
  );
}

export function HomeHeader({ ctx, onSettings }: { ctx: HomeContext; onSettings?: () => void }) {
  const { me } = useApp();
  useEffect(() => {
    if (!isDesktopUA() || !me) return;
    let live = true;
    void waitForBridge().then(bridge => {
      if (live && typeof bridge?.setAccount === 'function')
        (bridge.setAccount as (id: string, name: string, email: string, avatar: string) => void)(me.id, me.name, me.email, me.avatarUrl ?? '');
    });
    return () => { live = false; };
  }, [me]);
  const newItems: MenuItem[] = [
    { label: 'New document', icon: fileTextIcon, onSelect: () => ctx.create('docs') },
    { label: 'New spreadsheet', icon: tableIcon, onSelect: () => ctx.create('sheets') },
    { label: 'New presentation', icon: presentationChartIcon, onSelect: () => ctx.create('slides') }
  ];

  return (
    <header className="mv-home-hd">
      {!isDesktopUA() && <Link to="/" className="mv-home-brand" aria-label="Mauvely Office home">
        <Wordmark />
      </Link>}

      <nav aria-label="Menu" className="mv-home-nav">
        <TextMenu
          label="File"
          items={[
            ...(ctx.openFile ? [{ label: 'Open a file', icon: folderOpenIcon, onSelect: ctx.openFile }] : []),
            ...newItems.map((i, n) => (n === 0 ? { ...i, icon: filePlusIcon } : i)),
            { label: 'Import a file', icon: uploadSimpleIcon, onSelect: ctx.pickFile, separated: true },
            ...(onSettings ? [{ label: 'Office settings', icon: gearIcon, onSelect: onSettings, separated: true }] : [])
          ]}
        />
        <TextMenu
          label="View"
          items={[
            { label: 'Grid', ...(ctx.layout === 'grid' ? { icon: checkIcon } : {}), onSelect: () => ctx.setLayout('grid') },
            { label: 'List', ...(ctx.layout === 'list' ? { icon: checkIcon } : {}), onSelect: () => ctx.setLayout('list') },
            { label: 'System theme', ...(ctx.appearance === 'system' ? { icon: checkIcon } : {}), onSelect: () => ctx.setAppearance('system'), separated: true },
            { label: 'Light theme', ...(ctx.appearance === 'light' ? { icon: checkIcon } : {}), onSelect: () => ctx.setAppearance('light') },
            { label: 'Dark theme', ...(ctx.appearance === 'dark' ? { icon: checkIcon } : {}), onSelect: () => ctx.setAppearance('dark') }
          ]}
        />
        <TextMenu label="Help" items={[{ label: 'Documentation', onSelect: () => window.open(HELP_URL, '_blank', 'noopener') }]} />
      </nav>

      <div className="mv-home-search">
        <CommandSearch commands={HOME_COMMANDS} ctx={ctx} withDocuments />
      </div>

      <Button variant="secondary" icon={ctx.openFile ? folderOpenIcon : uploadSimpleIcon} className="hide-sm" onClick={ctx.openFile ?? ctx.pickFile}>
        {ctx.openFile ? 'Open' : 'Import'}
      </Button>
      <Menu
        label="New"
        icon={plusIcon}
        align="right"
        items={newItems}
        trigger={({ open, toggle, ref }) => (
          <Button ref={ref} variant="suite" icon={plusIcon} aria-haspopup="menu" aria-expanded={open} onClick={toggle}>
            New
          </Button>
        )}
      />
      {me && !isDesktopUA() ? (
        <a href={`${accountOrigin()}/account`} aria-label="Account" title="Your account" className="mv-home-account">
          <Avatar name={me.name} color="var(--violet-600)" src={me.avatarUrl} size={30} />
        </a>
      ) : null}
    </header>
  );
}
